<script setup lang="ts">
/**
 *
 * @name CertificateDetailsModal
 * @author @Jorel254
 * @version 1.0.0
 *
 * @description COMPONENTE MODAL QUE MUESTRA LOS DETALLES COMPLETOS DE UN CERTIFICADO
 *
 * responsabilidades:
 * R1: PERMITE VISUALIZAR LA INFORMACIÓN DETALLADA DE UN CERTIFICADO
 * R2: PERMITE MOSTRAR LA INFORMACIÓN DEL ALUMNO
 * R3: PERMITE MOSTRAR LA INFORMACIÓN DE LA CARRERA
 * R4: PERMITE MOSTRAR LA INFORMACIÓN DE LAS ASIGNATURAS
 * R5: PERMITE MOSTRAR LA INFORMACIÓN DEL RESPONSABLE
 * R6: PERMITE MOSTRAR LA INFORMACIÓN DEL CAMPUS
 * R7: PERMITE MOSTRAR LA CADENA ORIGINAL DEL CERTIFICADO
 *
 * @changelog 1.0.0 - 2025-06-17 - @Jorel254 - Versión inicial del componente
 */

import { ICertificate, ICertificatePackage } from '@shared/types'
import { ref, computed, watch, onUnmounted } from 'vue'
import { CGender, EGender } from '@shared/types/enums'
import { ref as dbRef, get } from 'firebase/database'
import { database } from '@/firebase/config'

/**
 * @description Props del componente
 * @property {boolean} modelValue - Controla la visibilidad del modal
 * @property {ICertificate | null} certificate - Certificado a mostrar
 * @property {boolean} isTestMode - Indica si el componente está en modo prueba
 */
const props = defineProps<{
	modelValue: boolean
	certificate: ICertificate | null
	isTestMode: boolean
	currentInstitution: string
}>()

/**
 * @description Emits del componente
 * @param {boolean} update:modelValue - Actualiza el valor del modal
 */
const getInstitutionPath = (path: string) => `institutions/${props.currentInstitution}${props.isTestMode ? '/testMode' : ''}${path}`
const emit = defineEmits<(e: 'update:modelValue', value: boolean) => void>()
const packageData = ref<ICertificatePackage | null>(null)
const db = database
let packageLoadRequestId = 0
/**
 * @description Computed property que controla la visibilidad del modal
 * @type {ComputedRef<boolean>}
 */
const showModal = computed({
	get: () => props.modelValue,
	set: value => emit('update:modelValue', value)
})

/**
 * @description Referencia local al certificado
 * @type {Ref<ICertificate | null>}
 */
const localCertificate = ref<ICertificate | null>(null)

/**
 * @description Referencia que controla la expansión de los detalles
 * @type {Ref<boolean>}
 */
const isExpanded = ref(false)

/**
 * @description Función que alterna la visibilidad de los detalles
 */
const toggleDetails = () => {
	isExpanded.value = !isExpanded.value
}

/**
 * @description Computed property que genera el nombre completo del alumno en mayúsculas
 * @returns {string} Nombre completo del alumno en formato "NOMBRE APELLIDO1 APELLIDO2"
 */
const fullName = computed(
	() =>
		`${localCertificate.value?.data.Alumno.nombre ? localCertificate.value?.data.Alumno.nombre.toUpperCase() : ''} ${localCertificate.value?.data.Alumno.primerApellido ? localCertificate.value?.data.Alumno.primerApellido.toUpperCase() : ''} ${localCertificate.value?.data.Alumno.segundoApellido ? localCertificate.value?.data.Alumno.segundoApellido.toUpperCase() : ''}`
)

/**
 * @description Computed property que obtiene el género del alumno
 * @returns {string} Género del alumno
 */
const genero = computed(() => CGender[localCertificate.value?.data.Alumno.idGenero as EGender])

/**
 * @description Función que formatea una fecha al formato local mexicano
 * @param {string | Date} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
const formatDate = (date: string | Date) => {
	if (!date) return 'No disponible'
	return new Date(date).toLocaleString('es-MX', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric'
	})
}

/**
 * @description Función que cierra el modal
 */
const closeModalFirma = () => {
	showModal.value = false
}

/**
 * @description Función que obtiene los datos del paquete de certificados
 * @param {number} packageId - ID del paquete
 */
const getDataPackage = async (packageId: number) => {
	const requestId = ++packageLoadRequestId
	packageData.value = null

	if (!packageId || packageId <= 0) return

	const packageRef = dbRef(db, getInstitutionPath(`/certificates/packages/${packageId}`))

	try {
		const snapshot = await get(packageRef)
		if (requestId !== packageLoadRequestId) return
		packageData.value = snapshot.exists() ? snapshot.val() : null
	} catch (error) {
		if (requestId !== packageLoadRequestId) return
		console.error('Error al obtener datos del paquete:', error)
		packageData.value = null
	}
}

onUnmounted(() => {
	packageLoadRequestId++
})

/**
 * @description Watcher que actualiza el certificado local cuando cambia el prop certificate
 * @param {ICertificate | null} newCertificate - Nuevo valor del certificado
 */
watch(
	() => props.certificate,
	newCertificate => {
		localCertificate.value = newCertificate ? { ...newCertificate } : null
		if (newCertificate) {
			getDataPackage(newCertificate?.package || 0)
		}
	},
	{ immediate: true }
)
</script>
<template>
	<VaModal v-model="showModal" hide-default-actions noDismiss noEscDismiss noOutsideDismiss background-color="transparent">
		<VaCard v-if="localCertificate" class="max-w-4xl mx-auto">
			<VaCardTitle :class="[props.isTestMode ? 'bg-gradient-paquete-test' : 'bg-gradient-paquete', 'rounded-t-xl']">
				<div class="row align-items-center">
					<div class="col-md-11 text-left">
						<VaIcon name="description" class="text-white" size="large" />
						<div>
							<h2 class="text-xl font-bold text-white m-0">Detalles del Certificado</h2>
							<p class="text-sm text-blue-100 m-0 text-white">Información completa del certificado</p>
						</div>
					</div>
					<div class="col-md-1 text-right ml2">
						<VaButton round icon="cancel" :color="!props.isTestMode ? '#0d6efd' : '#7f1d1d'" type="button" @click="closeModalFirma" style="color: white; font-weight: bold" />
					</div>
				</div>
			</VaCardTitle>

			<VaCardContent class="p-6">
				<!-- Información Principal del Estudiante -->
				<div class="mb-6">
					<div class="section-title">
						<VaIcon name="person" class="title-icon" />
						Información del Estudiante
					</div>
					<div class="student-card">
						<div class="gap-4 mb-4">
							<div class="student-avatar">
								<VaIcon name="person" class="student-icon" />
							</div>
							<div class="flex-1">
								<h3 class="student-name">{{ fullName }}</h3>
								<p class="student-career">{{ localCertificate.data.Carrera.nombreCarrera }}</p>
							</div>
						</div>

						<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div class="info-item">
								<VaIcon name="fingerprint" class="info-icon" />
								<span class="info-label">CURP:</span>
								<span class="info-value">{{ localCertificate?.data.Alumno.curp }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="badge" class="info-icon" />
								<span class="info-label">Número de Control:</span>
								<span class="info-value">{{ localCertificate.data.numeroControl }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="wc" class="info-icon" />
								<span class="info-label">Género:</span>
								<span class="info-value">{{ genero }}</span>
							</div>
							<div v-if="packageData" class="info-item">
								<VaIcon name="collections_bookmark" class="info-icon" />
								<span class="info-label">Paquete:</span>
								<span class="info-value">{{ packageData?.label }}</span>
							</div>
						</div>
					</div>
				</div>

				<!-- Información de Expedición -->
				<div class="mb-6">
					<div class="section-title">
						<VaIcon name="event" class="title-icon" />
						Información de Expedición
					</div>
					<div class="package-card">
						<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div class="info-item">
								<VaIcon name="fingerprint" class="info-icon" />
								<span class="info-label">Folio Control:</span>
								<span class="info-value">{{ localCertificate.folioControl || 'No disponible' }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="event" class="info-icon" />
								<span class="info-label">Fecha Expedición:</span>
								<span class="info-value">{{ formatDate(localCertificate.data.Expedicion.fecha) }}</span>
							</div>
							<div class="info-item">
								<VaIcon name="business" class="info-icon" />
								<span class="info-label">Institución:</span>
								<span class="info-value">{{ localCertificate.data.Ipes.nombreInstitucion }}</span>
							</div>
						</div>
					</div>
				</div>

				<!-- Botón para expandir detalles -->
				<div class="text-center mb-4">
					<button @click="toggleDetails" class="btn btn-primary flex items-center gap-2 mx-auto">
						<VaIcon :name="isExpanded ? 'expand_less' : 'expand_more'" />
						{{ isExpanded ? 'Ocultar detalles' : 'Ver más detalles' }}
					</button>
				</div>

				<!-- Detalles Expandidos -->
				<div v-if="isExpanded" class="space-y-6">
					<!-- Información del Responsable -->
					<div>
						<div class="section-title">
							<VaIcon name="groups" class="title-icon" />
							Información del Responsable
						</div>
						<div class="signer-card">
							<div class="gap-4 mb-4">
								<div class="signer-avatar mb-2">
									<VaIcon name="person" class="signer-icon" />
								</div>
								<div class="flex-1">
									<h4 class="font-semibold text-lg m-0">
										{{ localCertificate.data.Ipes.Responsable.nombre }}
										{{ localCertificate.data.Ipes.Responsable.primerApellido }}
										{{ localCertificate.data.Ipes.Responsable.segundoApellido }}
									</h4>
									<p class="text-gray-600 m-0">{{ localCertificate.data.Ipes.Responsable.cargo }}</p>
								</div>
							</div>
							<div class="info-item">
								<VaIcon name="fingerprint" class="info-icon" />
								<span class="info-label">CURP:</span>
								<span class="info-value">{{ localCertificate.data.Ipes.Responsable.curp }}</span>
							</div>
						</div>
					</div>

					<!-- Información de Asignaturas -->
					<div>
						<div class="section-title">
							<VaIcon name="school" class="title-icon" />
							Información de Asignaturas
						</div>

						<!-- Resumen de Asignaturas -->
						<div class="package-card mb-4">
							<div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
								<div class="text-center">
									<VaIcon name="book" class="text-2xl text-blue-500 mb-2" />
									<div class="text-sm font-semibold">Asignadas</div>
									<div class="text-lg font-bold text-blue-600">{{ localCertificate.data.Asignaturas.asignadas }}</div>
								</div>
								<div class="text-center">
									<VaIcon name="star" class="text-2xl text-green-500 mb-2" />
									<div class="text-sm font-semibold">Créditos Obtenidos</div>
									<div class="text-lg font-bold text-green-600">{{ localCertificate.data.Asignaturas.creditosObtenidos }}</div>
								</div>
								<div class="text-center">
									<VaIcon name="star_border" class="text-2xl text-orange-500 mb-2" />
									<div class="text-sm font-semibold">Total Créditos</div>
									<div class="text-lg font-bold text-orange-600">{{ localCertificate.data.Asignaturas.totalCreditos }}</div>
								</div>
								<div class="text-center">
									<VaIcon name="schedule" class="text-2xl text-purple-500 mb-2" />
									<div class="text-sm font-semibold">Ciclos</div>
									<div class="text-lg font-bold text-purple-600">{{ localCertificate.data.Asignaturas.numeroCiclos }}</div>
								</div>
								<div class="text-center">
									<VaIcon name="grade" class="text-2xl text-red-500 mb-2" />
									<div class="text-sm font-semibold">Promedio</div>
									<div class="text-lg font-bold text-red-600">{{ localCertificate.data.Asignaturas.promedio }}</div>
								</div>
								<div class="text-center">
									<VaIcon name="visibility" class="text-2xl text-indigo-500 mb-2" />
									<div class="text-sm font-semibold">Total</div>
									<div class="text-lg font-bold text-indigo-600">{{ localCertificate.data.Asignaturas.total }}</div>
								</div>
							</div>
						</div>
						<!-- Lista de Asignaturas -->
						<div class="students-grid">
							<div v-for="(asignatura, index) in localCertificate.data.Asignaturas.Asignatura" :key="index" class="student-card">
								<div class="gap-3 mb-3">
									<div class="student-avatar">
										<VaIcon name="book" class="student-icon" />
									</div>
									<div class="flex-1">
										<h5 class="font-semibold m-0">{{ asignatura.nombre }}</h5>
										<p class="text-sm text-gray-600 m-0">Clave: {{ asignatura.claveAsignatura }}</p>
									</div>
								</div>
								<div class="grid grid-cols-2 md:grid-cols-4 gap-3">
									<div class="text-center p-2 bg-gray-50 rounded">
										<div class="text-xs font-semibold text-gray-600">Ciclo</div>
										<div class="font-bold">{{ asignatura.ciclo }}</div>
									</div>
									<div class="text-center p-2 bg-gray-50 rounded">
										<div class="text-xs font-semibold text-gray-600">Créditos</div>
										<div class="font-bold">{{ asignatura.creditos }}</div>
									</div>
									<div class="text-center p-2 bg-gray-50 rounded">
										<div class="text-xs font-semibold text-gray-600">Calificación</div>
										<div class="font-bold">{{ asignatura.calificacion }}</div>
									</div>
									<div class="text-center p-2 bg-gray-50 rounded">
										<div class="text-xs font-semibold text-gray-600">Observaciones</div>
										<div class="font-bold text-sm">{{ asignatura.observaciones || 'N/A' }}</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Información del Campus -->
					<div>
						<div class="section-title">
							<VaIcon name="location_on" class="title-icon" />
							Información del Campus
						</div>
						<div class="package-card">
							<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div class="info-item">
									<VaIcon name="school" class="info-icon" />
									<span class="info-label">Campus:</span>
									<span class="info-value">{{ localCertificate.data.Ipes.campus }}</span>
								</div>
								<div class="info-item">
									<VaIcon name="location_on" class="info-icon" />
									<span class="info-label">Entidad Federativa:</span>
									<span class="info-value">{{ localCertificate.data.Ipes.entidadFederativa || 'No disponible' }}</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Información Técnica -->
					<div>
						<div class="section-title">
							<VaIcon name="code" class="title-icon" />
							Información Técnica
						</div>
						<div class="package-card">
							<div class="info-item">
								<VaIcon name="link" class="info-icon" />
								<span class="info-label">Cadena Original:</span>
							</div>
							<div class="mt-3 p-4 bg-gray-50 rounded-lg border">
								<code class="text-sm text-gray-700 break-all">{{ localCertificate.cadenaOriginal }}</code>
							</div>
						</div>
					</div>
				</div>
			</VaCardContent>
		</VaCard>
	</VaModal>
</template>

<style scoped>
/* Estilos base del modal */
.max-w-4xl {
	max-width: 56rem;
}

/* Títulos de sección */
.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Tarjetas base */
.student-card,
.signer-card,
.package-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
}

.student-card:hover,
.signer-card:hover,
.package-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Avatares */
.student-avatar,
.signer-avatar {
	width: 25px;
	height: 25px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.student-icon,
.signer-icon {
	color: white;
	font-size: 1rem;
}

/* Información del estudiante */
.student-name {
	font-size: 1.25rem;
	font-weight: 600;
	margin: 0 0 0.25rem 0;
	color: #1a2a3a;
}

.student-career {
	font-size: 0.875rem;
	margin: 0;
	color: #6c757d;
}

/* Elementos de información */
.info-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem;
	background: #f8f9fa;
	border-radius: 8px;
	border-left: 4px solid #007bff;
}

.info-icon {
	color: #007bff;
	font-size: 1.125rem;
	flex-shrink: 0;
}

.info-label {
	font-weight: 600;
	color: #1a2a3a;
	min-width: 120px;
}

.info-value {
	color: #6c757d;
	flex: 1;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.5rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Grid de estudiantes */
.students-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1rem;
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive design */
@media (max-width: 768px) {
	.students-grid {
		grid-template-columns: 1fr;
	}

	.info-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.info-label {
		min-width: auto;
	}

	.section-title {
		font-size: 1.125rem;
	}

	.student-avatar,
	.signer-avatar {
		width: 40px;
		height: 40px;
	}

	.student-icon,
	.signer-icon {
		font-size: 1rem;
	}
}

/* Utilidades */
.space-y-6 > * + * {
	margin-top: 1.5rem;
}

.mb-4 {
	margin-bottom: 1rem;
}

.mb-6 {
	margin-bottom: 1.5rem;
}

.mb-3 {
	margin-bottom: 0.75rem;
}

.mt-3 {
	margin-top: 0.75rem;
}

.p-6 {
	padding: 1.5rem;
}

.p-4 {
	padding: 1rem;
}

.p-2 {
	padding: 0.5rem;
}

.gap-2 {
	gap: 0.5rem;
}

.gap-3 {
	gap: 0.75rem;
}

.gap-4 {
	gap: 1rem;
}

.flex {
	display: flex;
}

.items-center {
	align-items: center;
}

.text-center {
	text-align: center;
}

.text-white {
	color: white;
}

.text-gray-600 {
	color: #6c757d;
}

.text-gray-700 {
	color: #374151;
}

.font-semibold {
	font-weight: 600;
}

.font-bold {
	font-weight: 700;
}

.text-lg {
	font-size: 1.125rem;
}

.text-xl {
	font-size: 1.25rem;
}

.text-sm {
	font-size: 0.875rem;
}

.text-xs {
	font-size: 0.75rem;
}

.m-0 {
	margin: 0;
}

.break-all {
	word-break: break-all;
}

.bg-gray-50 {
	background-color: #f9fafb;
}

.rounded {
	border-radius: 0.375rem;
}

.rounded-lg {
	border-radius: 0.5rem;
}

.border {
	border: 1px solid #e5e7eb;
}

/* Colores de iconos */
.text-blue-500 {
	color: #3b82f6;
}

.text-blue-600 {
	color: #2563eb;
}

.text-green-500 {
	color: #10b981;
}

.text-green-600 {
	color: #059669;
}

.text-orange-500 {
	color: #f97316;
}

.text-orange-600 {
	color: #ea580c;
}

.text-purple-500 {
	color: #8b5cf6;
}

.text-purple-600 {
	color: #7c3aed;
}

.text-red-500 {
	color: #ef4444;
}

.text-red-600 {
	color: #dc2626;
}

.text-indigo-500 {
	color: #6366f1;
}

.text-indigo-600 {
	color: #4f46e5;
}
.bg-gradient-paquete {
	background: linear-gradient(90deg, #2563eb 0%, #1e3a8a 100%);
}

.bg-gradient-paquete-test {
	background: linear-gradient(90deg, #dc2626 0%, #7f1d1d 100%);
}
</style>
